import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Map, { Marker, Popup } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useCity } from '../contexts/CityContext';
import { useAuth } from '../contexts/AuthContext';
import { useBusinesses } from '../hooks/useBusinesses';
import { useGeolocation } from '../hooks/useGeolocation';
import { useCluster } from '../hooks/useCluster';
import { CategoryFilter } from '../components/CategoryFilter';
import { BusinessCard } from '../components/BusinessCard';
import { SearchBar } from '../components/SearchBar';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';
import { BottomNav } from '../components/BottomNav';
import { BusinessMarker, ClusterMarker } from '../components/MapMarker';
import { BusinessPopupCard } from '../components/BusinessPopupCard';
import { resolveBoostVisual, markerZIndex, isOpenNow } from '../lib/mapConfig';
import api from '../services/api';

// LE-010 fix: style local com tiles raster OSM (gratuito, sem chave de API).
const MAP_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      maxzoom: 19,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [
    { id: 'osm', type: 'raster', source: 'osm' },
  ],
};

export function MapPage() {
  const { selectedCity } = useCity();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  // LE-037: true quando o usuário logado é o DONO do comércio selecionado.
  const [canManagePromo, setCanManagePromo] = useState(false);
  const [showList, setShowList] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  // LE-039: mobile — recolhe busca/filtros para o mapa dominar a tela (Galaxy M30 etc.)
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [zoom, setZoom] = useState(14);
  const [nearMe, setNearMe] = useState(false);
  const [onlyPromo, setOnlyPromo] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [favoriteLoadingId, setFavoriteLoadingId] = useState(null);

  const mapRef = useRef(null);
  // LE-021: protege contra race condition (resposta de marcador clicado antes).
  const selectedIdRef = useRef(null);

  const { position, error: locationError, loading: locationLoading, request: requestLocation } = useGeolocation();
  const { businesses, loading, error } = useBusinesses(
    selectedCity?.id, category, searchQuery, { nearMe, position }
  );

  const filteredBusinesses = useMemo(() => {
    return businesses.filter((b) => {
      // "Com promoção": sinal real do backend (hasPromotion/isBoosted).
      if (onlyPromo && !(b.hasPromotion || b.isBoosted)) return false;
      // "Aberto agora": horários reais da listagem (business_hours).
      if (onlyOpen && isOpenNow(b.hours) !== true) return false;
      return true;
    });
  }, [businesses, onlyPromo, onlyOpen]);

  const markers = useCluster(filteredBusinesses, zoom);

  const handleMarkerClick = useCallback((business) => {
    setSelectedBusiness(business);
    setSelectedPromo(null);
    selectedIdRef.current = business.id;
    mapRef.current?.flyTo({
      center: [business.lng, business.lat],
      zoom: 16,
      duration: 500,
    });

    // Comércios com promoção/impulso: busca a promoção ativa sob demanda.
    // (os horários já vêm na listagem — o indicador "Aberto agora" é imediato)
    if (business.isBoosted || business.hasPromotion) {
      setPromoLoading(true);
      setCanManagePromo(false);
      api.get(`/businesses/${business.id}`)
        .then(({ data }) => {
          if (selectedIdRef.current === business.id) {
            setSelectedPromo((data.promotions && data.promotions[0]) || null);
          }
        })
        .catch(() => {
          if (selectedIdRef.current === business.id) setSelectedPromo(null);
        })
        .finally(() => {
          if (selectedIdRef.current === business.id) setPromoLoading(false);
        });
      // LE-037: atalho "Renovar promoção" apenas para o DONO (gating de UX;
      // a autoridade real é o backend via POST /renew).
      if (user) {
        api.get('/merchant/businesses')
          .then(({ data }) => {
            if (selectedIdRef.current === business.id) {
              setCanManagePromo((data.data || []).some((b) => b.id === business.id));
            }
          })
          .catch(() => {
            if (selectedIdRef.current === business.id) setCanManagePromo(false);
          });
      }
    } else {
      setPromoLoading(false);
      setCanManagePromo(false);
    }
  }, [user]);

  const handleClusterClick = useCallback((cluster) => {
    mapRef.current?.flyTo({
      center: [cluster.lng, cluster.lat],
      zoom: Math.max((zoom || 14) + 2, 15),
      duration: 500,
    });
  }, [zoom]);

  const closePopup = useCallback(() => {
    setSelectedBusiness(null);
    setSelectedPromo(null);
    setPromoLoading(false);
    setCanManagePromo(false);
    selectedIdRef.current = null;
  }, []);

  // Favoritos (LE-011) — só para usuários autenticados; o estado é carregado
  // uma vez e atualizado localmente no toggle (PUT/DELETE /api/favorites).
  useEffect(() => {
    if (!user) {
      setFavoriteIds(new Set());
      return undefined;
    }
    let active = true;
    api.get('/favorites/ids')
      .then(({ data }) => { if (active) setFavoriteIds(new Set(data.ids || [])); })
      .catch(() => {});
    return () => { active = false; };
  }, [user]);

  const toggleFavorite = useCallback(async (businessId) => {
    if (!user) return;
    setFavoriteLoadingId(businessId);
    const wasFav = favoriteIds.has(businessId);
    try {
      if (wasFav) await api.delete(`/favorites/${businessId}`);
      else await api.put(`/favorites/${businessId}`);
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (wasFav) next.delete(businessId); else next.add(businessId);
        return next;
      });
    } catch {
      // silencioso — o estado não muda (o backend é a autoridade)
    } finally {
      setFavoriteLoadingId(null);
    }
  }, [user, favoriteIds]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSelectedBusiness(null);
    setSelectedPromo(null);
    setPromoLoading(false);
    setCanManagePromo(false);
    selectedIdRef.current = null;
  }, [category, searchQuery, selectedCity?.id, nearMe, onlyPromo, onlyOpen]);

  useEffect(() => {
    if (!user) setCanManagePromo(false);
  }, [user]);

  useEffect(() => {
    if (!selectedCity || !mapRef.current) return;
    mapRef.current.flyTo({
      center: [selectedCity.lng, selectedCity.lat],
      zoom: 14,
      duration: 500,
    });
    setZoom(14);
  }, [selectedCity?.id, selectedCity?.lat, selectedCity?.lng]);

  useEffect(() => {
    if (!position || !mapRef.current) return;
    mapRef.current.flyTo({
      center: [position.lng, position.lat],
      zoom: 15,
      duration: 700,
    });
  }, [position?.lat, position?.lng]);

  const handleNearMe = useCallback(() => {
    if (!nearMe && !position) requestLocation();
    setNearMe((v) => !v);
  }, [nearMe, position, requestLocation]);

  // LE-039: mobile — ao começar a explorar o mapa (arrastar/zoom), recolhe o
  // header automaticamente para o mapa dominar a tela. No desktop o chevron
  // nem existe (≥768px), então o guard por largura evita colapsar sem querer.
  const handleMapExploreStart = useCallback(() => {
    if (window.innerWidth < 768) setHeaderCollapsed(true);
  }, []);

  if (!selectedCity) return <Loading message="Carregando cidade..." />;

  const promoCount = filteredBusinesses.filter((b) => b.hasPromotion || b.isBoosted).length;

  return (
    <div
      className="nc-map-page"
      data-list={showList ? 'true' : 'false'}
      data-panel={panelCollapsed ? 'collapsed' : 'open'}
    >
      {/* Barra flutuante: cidade, busca, filtros rápidos e categorias */}
      <header
        className="nc-map-header"
        data-collapsed={headerCollapsed ? 'true' : 'false'}
      >
        <div className="nc-map-header__top">
          <span className="nc-map-header__city" title={`${selectedCity.name} / ${selectedCity.state}`}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {selectedCity.name} / {selectedCity.state}
          </span>
          <button
            className="nc-map-header__link"
            onClick={() => navigate('/cidade')}
            aria-label="Trocar de cidade"
            title="Trocar de cidade"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            Trocar
          </button>
          <button
            className="nc-map-header__link nc-map-header__link--event"
            onClick={() => navigate('/eventos')}
            aria-label="Eventos da cidade"
            title="Eventos da cidade"
          >
            🎉 Eventos
          </button>
          {/* LE-039: /mapa é standalone (tela cheia) — no desktop a navegação
              principal fica no header flutuante (no mobile existe a BottomNav) */}
          <div className="nc-map-header__nav" role="group" aria-label="Navegação principal">
            <button
              type="button"
              className="nc-map-header__nav-item"
              onClick={() => navigate('/feed')}
            >
              Feed
            </button>
            <button
              type="button"
              className="nc-map-header__nav-item"
              onClick={() => navigate('/entregas')}
            >
              Entregas
            </button>
            <button
              type="button"
              className="nc-map-header__nav-item"
              onClick={() => navigate('/perfil')}
            >
              Perfil
            </button>
          </div>
          <button
            type="button"
            className="nc-map-header__toggle"
            onClick={() => setShowList(!showList)}
            aria-label={showList ? 'Voltar para o mapa' : 'Ver lista de comércios'}
            aria-pressed={showList}
          >
            {showList ? 'Ver mapa' : 'Ver lista'}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d={showList ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
            </svg>
          </button>
          {/* LE-039: mobile — recolhe busca/filtros para o mapa dominar a tela */}
          <button
            type="button"
            className="nc-map-header__collapse"
            onClick={() => setHeaderCollapsed((v) => !v)}
            aria-label={headerCollapsed ? 'Expandir busca e filtros' : 'Recolher busca e filtros'}
            aria-expanded={!headerCollapsed}
            title={headerCollapsed ? 'Expandir busca e filtros' : 'Recolher busca e filtros'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d={headerCollapsed ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
            </svg>
          </button>
        </div>

        <SearchBar
          placeholder="Buscar comércio, produto, rua..."
          value={search}
          onChange={setSearch}
        />

        {/* LE-039: filtros rápidos + categorias numa ÚNICA linha rolável no
            mobile (Google Maps style) — o header não empilha 4 linhas sobre
            o mapa. No desktop ficam lado a lado (row wrap). */}
        <div className="nc-map-header__filters">
        <div className="nc-map-header__quickfilters" role="group" aria-label="Filtros rápidos">
          <button
            type="button"
            className={`nc-quick-chip${nearMe ? ' nc-quick-chip--active' : ''}`}
            onClick={handleNearMe}
            aria-pressed={nearMe}
            title={nearMe ? 'Mostrar todos' : 'Filtrar estabelecimentos perto de mim'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
            Perto de mim
          </button>
          <button
            type="button"
            className={`nc-quick-chip${onlyPromo ? ' nc-quick-chip--active' : ''}`}
            onClick={() => setOnlyPromo((v) => !v)}
            aria-pressed={onlyPromo}
            title="Mostrar apenas estabelecimentos com promoção"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4-3.9-3.8 5.4-.8z" />
            </svg>
            Com promoção
          </button>
          <button
            type="button"
            className={`nc-quick-chip${onlyOpen ? ' nc-quick-chip--active' : ''}`}
            onClick={() => setOnlyOpen((v) => !v)}
            aria-pressed={onlyOpen}
            title="Mostrar apenas estabelecimentos abertos agora"
          >
            🕐 Aberto agora
          </button>
        </div>

        <div className="nc-map-header__categories">
          <CategoryFilter active={category} onChange={setCategory} />
        </div>
        </div>
      </header>

      {/* Mapa + painel de lista */}
      <div className="nc-map-body">
        <div className="nc-map-canvas">
          {error && (
            <div role="alert" className="nc-map-error">
              <span aria-hidden="true">⚠️</span> {error}
            </div>
          )}
          {loading && (
            <div className="nc-map-loading" aria-live="polite">
              <Loading message="Carregando comércios..." />
            </div>
          )}

          <Map
            ref={mapRef}
            initialViewState={{
              longitude: selectedCity.lng,
              latitude: selectedCity.lat,
              zoom: 14,
            }}
            onMove={(evt) => {
              const z = evt.viewState?.zoom;
              if (z != null && z !== zoom) setZoom(z);
            }}
            onDragStart={handleMapExploreStart}
            onZoomStart={handleMapExploreStart}
            style={{ width: '100%', height: '100%' }}
            aria-label={`Mapa de comércios de ${selectedCity.name}`}
            mapStyle={MAP_STYLE}
          >
            {position && (
              <Marker longitude={position.lng} latitude={position.lat} anchor="center">
                <div
                  role="img"
                  aria-label="Sua localização atual"
                  className="nc-map-user-marker nc-pulse"
                />
              </Marker>
            )}

            {markers.map((item) =>
              item.type === 'cluster' ? (
                <Marker
                  key={`cluster-${item.id}`}
                  longitude={item.lng}
                  latitude={item.lat}
                  anchor="center"
                  style={{ zIndex: 2000 }}
                >
                  <ClusterMarker cluster={item} onClick={() => handleClusterClick(item)} />
                </Marker>
              ) : (
                <Marker
                  key={item.business.id}
                  longitude={item.business.lng}
                  latitude={item.business.lat}
                  anchor="bottom"
                  style={{ zIndex: markerZIndex(resolveBoostVisual(item.business)) }}
                >
                  <BusinessMarker business={item.business} onClick={() => handleMarkerClick(item.business)} />
                </Marker>
              )
            )}

            {selectedBusiness && (
              <Popup
                longitude={selectedBusiness.lng}
                latitude={selectedBusiness.lat}
                anchor="bottom"
                onClose={closePopup}
                closeButton
                closeOnClick={false}
                className="nc-map-popup"
              >
                <BusinessPopupCard
                  business={selectedBusiness}
                  promo={selectedPromo}
                  promoLoading={promoLoading}
                  canManagePromo={canManagePromo}
                  hours={selectedBusiness.hours}
                  favorited={favoriteIds.has(selectedBusiness.id)}
                  favoriteLoading={favoriteLoadingId === selectedBusiness.id}
                  onToggleFavorite={user ? () => toggleFavorite(selectedBusiness.id) : null}
                />
              </Popup>
            )}
          </Map>

          <div className="nc-map-controls">
            {locationLoading && (
              <div role="status" aria-live="polite" className="nc-visually-hidden">
                Solicitando sua localização
              </div>
            )}
            {locationError && (
              <div role="alert" className="nc-map-location-error">
                Não foi possível obter sua localização. Você ainda pode usar o mapa normalmente.
              </div>
            )}
            <button
              type="button"
              onClick={requestLocation}
              disabled={locationLoading}
              aria-label="Usar minha localização"
              title="Usar minha localização"
              className="nc-map-location-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
              </svg>
              <span>{locationLoading ? 'Localizando...' : 'Minha localização'}</span>
            </button>
          </div>

          {panelCollapsed && (
            <button
              type="button"
              className="nc-map-panel-reopen"
              onClick={() => setPanelCollapsed(false)}
              aria-label="Abrir painel de comércios"
              title="Abrir painel de comércios"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              <span>Comércios</span>
            </button>
          )}
        </div>

        <aside className="nc-map-list" aria-label="Lista de comércios">
          <div className="nc-map-list__head">
            <div className="nc-map-list__title">
              <strong>{onlyPromo ? 'Promoções' : onlyOpen ? 'Abertos agora' : 'Comércios'}</strong>
              <span className="nc-map-list__count">{filteredBusinesses.length}</span>
            </div>
            <button
              type="button"
              className="nc-map-list__collapse"
              onClick={() => setPanelCollapsed((v) => !v)}
              aria-label={panelCollapsed ? 'Abrir painel' : 'Recolher painel'}
              title={panelCollapsed ? 'Abrir painel' : 'Recolher painel'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={panelCollapsed ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'} />
              </svg>
            </button>
          </div>

          <div className="nc-map-list__scroll">
            {loading ? (
              <Loading />
            ) : (
              <div className="nc-map-list__items">
                {error && (
                  <div role="alert" className="nc-map-list__error">{error}</div>
                )}
                {filteredBusinesses.map((b) => (
                  <BusinessCard key={b.id} business={b} />
                ))}
                {filteredBusinesses.length === 0 && !error && (
                  onlyPromo ? (
                    <EmptyState
                      icon="🏷️"
                      title="Nenhuma promoção encontrada"
                      description="Ainda não há promoções ativas por aqui. Volte em breve!"
                    />
                  ) : (
                    <EmptyState
                      icon="🏪"
                      title="Nenhum comércio encontrado"
                      description="Tente ajustar a busca ou os filtros."
                    />
                  )
                )}
                {!loading && !error && filteredBusinesses.length > 0 && promoCount > 0 && (
                  <p className="nc-map-list__hint">
                    {promoCount} {promoCount === 1 ? 'estabelecimento com promoção' : 'estabelecimentos com promoção'} em destaque no mapa.
                  </p>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      <BottomNav />
    </div>
  );
}
